import { select } from 'redux-saga/effects';
import { all, call, put, takeLatest } from 'typed-redux-saga/macro';
import {
    CHANGE_NODE_STATUS_PATH,
    NODES_URL,
} from '../../constants/api.constants';
import { apiRequestWrapper } from '../../utils/api/api.utils';
import { setIsError } from '../error/error.action';
import { setIsLoading } from '../loading/loading.action';
import { GetProjectStart } from '../project/project.action';
import { selectProjectSelected } from '../project/project.selector';
import {
    changeNodeStatusFailed,
    ChangeNodeStatusStart,
    changeNodeStatusSuccess,
    getMyNodesFailed,
    getMyNodesStart,
    GetMyNodesStart,
    getMyNodesSuccess,
    getNodeFailed,
    getNodeSuccess,
} from './node.action';
import { NODE_ACTION_TYPES } from './node.type';

export function* getMyNodes({ payload: { project } }: GetMyNodesStart) {
    yield* put(setIsLoading(true));
    try {
        let url = `${NODES_URL}me/`;
        if (project) {
            url = `${NODES_URL}me/?project=${project}`;
        }
        const nodes = yield* call(apiRequestWrapper, url, 'GET', null);

        if (nodes) {
            yield* put(getMyNodesSuccess(nodes));
        }
        yield* put(setIsLoading(false));
    } catch (error: any) {
        const errorResponse =
            error.response?.data?.error_description ??
            error.response?.data?.error ??
            error.response?.data?.detail ??
            error.message ??
            'Errore server';
        yield* put(getMyNodesFailed(errorResponse));
        yield* put(setIsLoading(false));
        yield* put(setIsError(true, errorResponse));
    }
}

export function* getNode({ payload: { nodeId } }: GetProjectStart): Generator {
    const project = yield select(selectProjectSelected);
    yield* put(setIsLoading(true));
    try {
        const node = yield* call(
            apiRequestWrapper,
            `${NODES_URL}${nodeId}/`,
            'GET',
            null
        );

        if (node) {
            yield* put(getNodeSuccess(node));
        }
        yield* put(setIsLoading(false));
    } catch (error: any) {
        const errorResponse =
            error.response?.data?.error_description ??
            error.response?.data?.error ??
            error.response?.data?.detail ??
            error.message ??
            'Errore server';
        if (error.status === 404) {
            yield* put(getMyNodesStart(project ? (project as any).id : null));
            yield* put(getNodeFailed(errorResponse));
            yield* put(setIsLoading(false));
            return;
        }
        yield* put(getNodeFailed(errorResponse));
        yield* put(setIsLoading(false));
        yield* put(setIsError(true, errorResponse));
    }
}

export function* changeNodeStatus({
    payload: { nodeId, status, nextStepsToActivate, onComplete },
}: ChangeNodeStatusStart) {
    yield* put(setIsLoading(true));
    try {
        const requestBody = { status, next_steps_to_activate: nextStepsToActivate };

        const node = yield* call(
            apiRequestWrapper,
            `${NODES_URL}${nodeId}/${CHANGE_NODE_STATUS_PATH}`,
            'PUT',
            requestBody
        );

        if (node) {
            yield* put(changeNodeStatusSuccess(node));
        }
        yield* put(setIsLoading(false));
        onComplete();
    } catch (error: any) {
        const errorResponse =
            error.response?.data?.error_description ??
            error.response?.data?.error ??
            error.response?.data?.detail ??
            error.message ??
            'Errore server';
        yield* put(changeNodeStatusFailed(errorResponse));
        yield* put(setIsLoading(false));
        yield* put(setIsError(true, errorResponse));
    }
}

export function* onGetMyNodesStart() {
    yield* takeLatest(NODE_ACTION_TYPES.GET_MY_NODES_START, getMyNodes);
}

export function* onGetNodeStart() {
    yield* takeLatest(NODE_ACTION_TYPES.GET_NODE_START, getNode);
}

export function* onChangeNodeStatus() {
    yield* takeLatest(
        NODE_ACTION_TYPES.CHANGE_NODE_STATUS_START,
        changeNodeStatus
    );
}

export function* nodeSaga() {
    yield* all([
        call(onGetMyNodesStart),
        call(onGetNodeStart),
        call(onChangeNodeStatus),
    ]);
}
